import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const CITIES = ['Adana','Adıyaman','Afyonkarahisar','Ağrı','Aksaray','Amasya','Ankara','Antalya','Ardahan','Artvin','Aydın','Balıkesir','Bartın','Batman','Bayburt','Bilecik','Bingöl','Bitlis','Bolu','Burdur','Bursa','Çanakkale','Çankırı','Çorum','Denizli','Diyarbakır','Düzce','Edirne','Elazığ','Erzincan','Erzurum','Eskişehir','Gaziantep','Giresun','Gümüşhane','Hakkari','Hatay','Iğdır','Isparta','İstanbul','İzmir','Kahramanmaraş','Karabük','Karaman','Kars','Kastamonu','Kayseri','Kırıkkale','Kırklareli','Kırşehir','Kilis','Kocaeli','Konya','Kütahya','Malatya','Manisa','Mardin','Mersin','Muğla','Muş','Nevşehir','Niğde','Ordu','Osmaniye','Rize','Sakarya','Samsun','Şanlıurfa','Siirt','Sinop','Şırnak','Sivas','Tekirdağ','Tokat','Trabzon','Tunceli','Uşak','Van','Yalova','Yozgat','Zonguldak'];
const SOIL_TYPES = ['Killi','Kumlu','Tınlı','Humuslu','Kireçli','Karma'];
const WORK_TYPES_LIST = ['Hasat','Budama','Sulama','Ekim/Dikim','İlaçlama','Çapalama','Gübreleme','Paketleme','Taşıma','Diğer'];

export default function OnboardingPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const totalSteps = 7;

  // Step 2 — Farm
  const [farm, setFarm] = useState({ name: '', city: '', district: '', farmType: 'mixed', isCompany: false, taxNumber: '', totalLandDecare: '' });

  // Step 3 — Fields
  const [fields, setFields] = useState([]);
  const [fieldForm, setFieldForm] = useState({ name: '', sizeDecare: '', soilType: '', currentCrop: '' });

  // Step 4 — Products
  const [catalog, setCatalog] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [productTab, setProductTab] = useState('sell');
  const [productSearch, setProductSearch] = useState('');

  // Step 5 — Favourites
  const [favourites, setFavourites] = useState(new Set());

  // Step 6 — Workers
  const [workers, setWorkers] = useState([]);
  const [workerForm, setWorkerForm] = useState({ firstName: '', lastName: '', phone: '', workTypes: [], defaultDailyRate: '' });

  const [saving, setSaving] = useState(false);

  // Load farm data on mount
  useEffect(() => {
    api.get('/settings/farm').then(r => {
      const f = r.data.data;
      if (f) setFarm({ name: f.name || '', city: f.city || '', district: f.district || '', farmType: f.farm_type || 'mixed', isCompany: f.is_company || false, taxNumber: f.tax_number || '', totalLandDecare: f.total_land_decare || '' });
    }).catch(() => {});
  }, []);

  // Load catalog for step 4
  const loadCatalog = useCallback(() => {
    const params = new URLSearchParams();
    if (productSearch) params.set('search', productSearch);
    if (farm.farmType && farm.farmType !== 'mixed') params.set('farmType', farm.farmType);
    api.get(`/farm-products/catalog?${params}`).then(r => {
      const data = r.data.data || [];
      setCatalog(data);
      const pre = new Set();
      data.forEach(p => { if (p.is_selected) pre.add(p.id); });
      setSelectedProducts(prev => { const m = new Set(prev); pre.forEach(id => m.add(id)); return m; });
    }).catch(() => {});
  }, [productSearch, farm.farmType]);

  useEffect(() => { if (step === 4) loadCatalog(); }, [step, loadCatalog]);

  const finish = () => {
    localStorage.removeItem('teldolap_onboarding_pending');
    navigate('/');
  };

  const saveFarm = async () => {
    setSaving(true);
    try {
      // First fetch current farm data to preserve existing fields
      const current = await api.get('/settings/farm');
      const existing = current.data.data || {};
      await api.put('/settings/farm', {
        name: farm.name || existing.name,
        ownerName: existing.owner_name || user?.name,
        phone: existing.phone || null,
        email: existing.email || null,
        address: existing.address || null,
        city: farm.city || existing.city,
        district: farm.district || existing.district,
        isCompany: farm.isCompany,
        taxNumber: farm.taxNumber || existing.tax_number,
        farmType: farm.farmType || existing.farm_type,
        totalLandDecare: farm.totalLandDecare || existing.total_land_decare,
      });
      setStep(3);
    } catch (err) {
      console.error('Farm save error:', err.response?.data || err.message);
      toast.error(err.response?.data?.error || err.response?.data?.detail || t('onboarding.save_failed'));
    }
    finally { setSaving(false); }
  };

  const addField = async () => {
    if (!fieldForm.name) return;
    try {
      const r = await api.post('/farm/fields', fieldForm);
      setFields(prev => [...prev, r.data]);
      setFieldForm({ name: '', sizeDecare: '', soilType: '', currentCrop: '' });
    } catch { toast.error('Tarla eklenemedi'); }
  };

  const saveProducts = async () => {
    setSaving(true);
    try {
      await api.post('/farm-products/bulk', { masterProductIds: Array.from(selectedProducts) });
      setStep(5);
    } catch { toast.error('Ürünler kaydedilemedi'); }
    finally { setSaving(false); }
  };

  const saveFavourites = async () => {
    setSaving(true);
    try {
      for (const id of favourites) {
        await api.put(`/farm-products/${id}/favourite`, { isFavourite: true });
      }
      setStep(6);
    } catch { toast.error('Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  const addWorker = async () => {
    if (!workerForm.firstName || !workerForm.lastName) return;
    try {
      const r = await api.post('/workers', workerForm);
      setWorkers(prev => [...prev, r.data]);
      setWorkerForm({ firstName: '', lastName: '', phone: '', workTypes: [], defaultDailyRate: '' });
    } catch { toast.error('İşçi eklenemedi'); }
  };

  const toggleProduct = (id) => setSelectedProducts(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleFav = (id) => setFavourites(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleWorkType = (wt) => setWorkerForm(f => ({ ...f, workTypes: f.workTypes.includes(wt) ? f.workTypes.filter(w => w !== wt) : [...f.workTypes, wt] }));

  const filteredCatalog = catalog.filter(p => {
    if (productTab === 'sell') return p.usage_type === 'sell' || p.usage_type === 'both';
    return p.usage_type === 'buy' || p.usage_type === 'both';
  });

  const progress = (step / totalSteps) * 100;
  const firstName = user?.name?.split(' ')[0] || '';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)', display: 'flex', flexDirection: 'column' }}>
      {/* Progress bar */}
      <div style={{ height: 4, background: 'var(--border)' }}>
        <div style={{ height: '100%', width: `${progress}%`, background: 'var(--green-mid)', transition: 'width 0.3s' }} />
      </div>

      <div style={{ flex: 1, padding: '20px 16px 80px', maxWidth: 480, margin: '0 auto', width: '100%' }}>

        {/* STEP 1 — Welcome */}
        {step === 1 && (
          <div style={{ textAlign: 'center', paddingTop: 40 }}>
            <div style={{ fontSize: '4rem', marginBottom: 16 }}>🌱</div>
            <h1 style={{ fontFamily: 'Playfair Display', color: 'var(--green-dark)', margin: '0 0 8px' }}>
              {t('onboarding.welcome_title', { name: firstName })}
            </h1>
            <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: 32 }}>
              {t('onboarding.welcome_subtitle')}
            </p>
            <div style={{ textAlign: 'left', marginBottom: 32 }}>
              {[['🌾',t('onboarding.feature_harvest')],['💰',t('onboarding.feature_sales')],['📊',t('onboarding.feature_analytics')]].map(([e,txt],i) => (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <span style={{ fontSize: '1.5rem' }}>{e}</span>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{txt}</span>
                </div>
              ))}
            </div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }}
              onClick={() => setStep(2)}>{t('onboarding.start_button')}</button>
            <div onClick={finish} style={{ textAlign: 'center', fontSize: '0.82rem', color: '#888', cursor: 'pointer' }}>
              {t('onboarding.skip_link')}
            </div>
          </div>
        )}

        {/* STEP 2 — Farm Info */}
        {step === 2 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 16 }}><span style={{ fontSize: '3rem' }}>🏡</span></div>
            <h2 style={{ fontFamily: 'Playfair Display', color: 'var(--green-dark)', textAlign: 'center', marginBottom: 4 }}>{t('onboarding.step_farm_title')}</h2>
            <p style={{ textAlign: 'center', color: '#888', fontSize: '0.82rem', marginBottom: 16 }}>{t('onboarding.step_farm_subtitle')}</p>
            <div className="form-group"><label>Çiftlik Adı *</label><input value={farm.name} onChange={e => setFarm(f => ({ ...f, name: e.target.value }))} required /></div>
            <div className="form-row">
              <div className="form-group"><label>İl</label><select value={farm.city} onChange={e => setFarm(f => ({ ...f, city: e.target.value }))}><option value="">Seçin</option>{CITIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
              <div className="form-group"><label>İlçe</label><input value={farm.district} onChange={e => setFarm(f => ({ ...f, district: e.target.value }))} /></div>
            </div>
            <div className="form-group"><label>İşletme Türü</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {[['crop','🌾 Tarla / Tahıl'],['orchard','🍎 Bahçe / Meyve'],['livestock','🐄 Hayvancılık'],['mixed','🌿 Karma']].map(([v,l]) => (
                  <button key={v} type="button" onClick={() => setFarm(f => ({ ...f, farmType: v }))}
                    style={{ padding: '10px 8px', borderRadius: 8, border: farm.farmType === v ? '2px solid var(--green-mid)' : '1px solid var(--border)',
                      background: farm.farmType === v ? 'var(--green-pale)' : '#fff', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', textAlign: 'center' }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {farm.farmType && (
              <div style={{ background: '#f0f7ee', borderRadius: 8, padding: '8px 10px', margin: '0 0 10px', fontSize: '0.78rem', color: '#2d5a27', lineHeight: 1.4 }}>
                {{
                  orchard: '🍎 Meyve/Bahçe çiftliği seçildi. Sonraki adımda elma, kiraz, zeytin gibi ürünler öncelikli gösterilecek.',
                  crop: '🌾 Tarla çiftliği seçildi. Sonraki adımda buğday, mısır, ayçiçeği gibi ürünler öncelikli gösterilecek.',
                  livestock: '🐄 Hayvancılık seçildi. Sonraki adımda süt, et, yem ürünleri öncelikli gösterilecek.',
                  mixed: '🌿 Karma çiftlik seçildi. Tüm ürün kategorileri gösterilecek.',
                }[farm.farmType]}
              </div>
            )}
            <div className="form-group"><label>Toplam Arazi (dekar)</label><input type="number" value={farm.totalLandDecare} onChange={e => setFarm(f => ({ ...f, totalLandDecare: e.target.value }))} /></div>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setStep(1)}>← Geri</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} onClick={saveFarm} disabled={saving || !farm.name}>
                {saving ? 'Kaydediliyor...' : 'Kaydet ve Devam →'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — Fields */}
        {step === 3 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 16 }}><span style={{ fontSize: '3rem' }}>🗺️</span></div>
            <h2 style={{ fontFamily: 'Playfair Display', color: 'var(--green-dark)', textAlign: 'center', marginBottom: 4 }}>Tarlalarınızı ekleyin</h2>
            <p style={{ textAlign: 'center', color: '#888', fontSize: '0.82rem', marginBottom: 16 }}>Hangi tarla veya bahçeleriniz var?</p>
            {fields.map((f, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: '#f0f7ee', borderRadius: 8, marginBottom: 4, fontSize: '0.85rem' }}>
                <span>🌿 {f.name} {f.size_decare ? `(${f.size_decare} da)` : ''}</span>
              </div>
            ))}
            <div className="card" style={{ padding: 12, marginBottom: 12 }}>
              <div className="form-group"><label>Tarla Adı</label><input value={fieldForm.name} onChange={e => setFieldForm(f => ({ ...f, name: e.target.value }))} placeholder="Kuzey Tarlası..." /></div>
              <div className="form-row">
                <div className="form-group"><label>Dekar</label><input type="number" value={fieldForm.sizeDecare} onChange={e => setFieldForm(f => ({ ...f, sizeDecare: e.target.value }))} /></div>
                <div className="form-group"><label>Toprak</label><select value={fieldForm.soilType} onChange={e => setFieldForm(f => ({ ...f, soilType: e.target.value }))}><option value="">Seçin</option>{SOIL_TYPES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              </div>
              <button type="button" onClick={addField} disabled={!fieldForm.name}
                style={{ width: '100%', padding: 8, background: 'var(--green-pale)', border: 'none', borderRadius: 8, fontWeight: 700, color: 'var(--green-mid)', cursor: 'pointer' }}>
                ➕ Ekle
              </button>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setStep(2)}>← Geri</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} onClick={() => setStep(4)}>Devam →</button>
            </div>
            <div onClick={() => setStep(4)} style={{ textAlign: 'center', fontSize: '0.82rem', color: '#888', cursor: 'pointer', marginTop: 8 }}>Tarla yok, atla →</div>
          </div>
        )}

        {/* STEP 4 — Products */}
        {step === 4 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 12 }}><span style={{ fontSize: '3rem' }}>🌾</span></div>
            <h2 style={{ fontFamily: 'Playfair Display', color: 'var(--green-dark)', textAlign: 'center', marginBottom: 4 }}>Ürünlerinizi seçin</h2>
            <p style={{ textAlign: 'center', color: '#888', fontSize: '0.82rem', marginBottom: 8 }}>{selectedProducts.size} ürün seçildi</p>
            <div className="settings-tabs" style={{ marginBottom: 8 }}>
              <button className={`settings-tab${productTab === 'sell' ? ' active' : ''}`} onClick={() => setProductTab('sell')}>🌾 Ürettiklerim</button>
              <button className={`settings-tab${productTab === 'buy' ? ' active' : ''}`} onClick={() => setProductTab('buy')}>🛒 Aldıklarım</button>
            </div>
            <input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Ürün ara..." style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 8, boxSizing: 'border-box', fontSize: '0.85rem' }} />
            <div style={{ maxHeight: 300, overflow: 'auto' }}>
              {filteredCatalog.length === 0 && (
                <div className="empty-state"><p>Bu tür için ürün bulunamadı</p></div>
              )}
              {Object.entries(filteredCatalog.reduce((acc, p) => {
                const k = p.subcategory || p.product_type || 'Diğer';
                (acc[k] = acc[k] || []).push(p); return acc;
              }, {})).map(([sub, rows]) => (
                <div key={sub}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7a9472', padding: '8px 4px 2px' }}>{sub}</div>
                  {rows.map(p => (
                    <div key={p.id} onClick={() => toggleProduct(p.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, marginBottom: 3, cursor: 'pointer',
                        background: selectedProducts.has(p.id) ? 'var(--green-pale)' : '#fff',
                        border: selectedProducts.has(p.id) ? '2px solid var(--green-mid)' : '1px solid var(--border)' }}>
                      <span style={{ fontSize: '1rem' }}>{selectedProducts.has(p.id) ? '✅' : '➕'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{p.name}</div>
                        <div style={{ fontSize: '0.7rem', color: '#999' }}>{p.product_type} · {p.unit}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setStep(3)}>← Geri</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} onClick={saveProducts} disabled={saving || selectedProducts.size === 0}>
                {saving ? 'Kaydediliyor...' : 'Devam →'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 5 — Favourites */}
        {step === 5 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 12 }}><span style={{ fontSize: '3rem' }}>⭐</span></div>
            <h2 style={{ fontFamily: 'Playfair Display', color: 'var(--green-dark)', textAlign: 'center', marginBottom: 4 }}>Ana ürünleriniz</h2>
            <p style={{ textAlign: 'center', color: '#888', fontSize: '0.82rem', marginBottom: 16 }}>Seçtikleriniz formlarda en üstte görünür.</p>
            {catalog.filter(p => selectedProducts.has(p.id) && (p.usage_type === 'sell' || p.usage_type === 'both')).map(p => (
              <div key={p.id} onClick={() => toggleFav(p.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, marginBottom: 3, cursor: 'pointer',
                  background: favourites.has(p.id) ? '#fffcf0' : '#fff',
                  border: favourites.has(p.id) ? '2px solid #e67e22' : '1px solid var(--border)' }}>
                <span>{favourites.has(p.id) ? '⭐' : '☆'}</span>
                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{p.name}</span>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setStep(4)}>← Geri</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} onClick={saveFavourites} disabled={saving}>
                {saving ? 'Kaydediliyor...' : 'Devam →'}
              </button>
            </div>
            <div onClick={() => setStep(6)} style={{ textAlign: 'center', fontSize: '0.82rem', color: '#888', cursor: 'pointer', marginTop: 8 }}>Atla →</div>
          </div>
        )}

        {/* STEP 6 — Workers */}
        {step === 6 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 12 }}><span style={{ fontSize: '3rem' }}>👷</span></div>
            <h2 style={{ fontFamily: 'Playfair Display', color: 'var(--green-dark)', textAlign: 'center', marginBottom: 4 }}>İşçilerinizi ekleyin</h2>
            <p style={{ textAlign: 'center', color: '#888', fontSize: '0.82rem', marginBottom: 16 }}>Düzenli çalıştırdığınız işçileri kaydedin.</p>
            {workers.map((w, i) => (
              <div key={i} style={{ padding: '6px 10px', background: '#f0f7ee', borderRadius: 8, marginBottom: 4, fontSize: '0.85rem' }}>
                👷 {w.first_name} {w.last_name} {w.default_daily_rate ? `· ₺${w.default_daily_rate}/gün` : ''}
              </div>
            ))}
            <div className="card" style={{ padding: 12, marginBottom: 12 }}>
              <div className="form-row">
                <div className="form-group"><label>Ad</label><input value={workerForm.firstName} onChange={e => setWorkerForm(f => ({ ...f, firstName: e.target.value }))} /></div>
                <div className="form-group"><label>Soyad</label><input value={workerForm.lastName} onChange={e => setWorkerForm(f => ({ ...f, lastName: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Telefon</label><input value={workerForm.phone} onChange={e => setWorkerForm(f => ({ ...f, phone: e.target.value }))} /></div>
                <div className="form-group"><label>Günlük (₺)</label><input type="number" value={workerForm.defaultDailyRate} onChange={e => setWorkerForm(f => ({ ...f, defaultDailyRate: e.target.value }))} /></div>
              </div>
              <div className="form-group"><label>İş Türleri</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {WORK_TYPES_LIST.map(wt => (
                    <button key={wt} type="button" onClick={() => toggleWorkType(wt)}
                      style={{ padding: '3px 6px', borderRadius: 4, fontSize: '0.7rem', cursor: 'pointer',
                        border: workerForm.workTypes.includes(wt) ? '2px solid var(--green-mid)' : '1px solid var(--border)',
                        background: workerForm.workTypes.includes(wt) ? 'var(--green-pale)' : '#fff' }}>
                      {wt}
                    </button>
                  ))}
                </div>
              </div>
              <button type="button" onClick={addWorker} disabled={!workerForm.firstName || !workerForm.lastName}
                style={{ width: '100%', padding: 8, background: 'var(--green-pale)', border: 'none', borderRadius: 8, fontWeight: 700, color: 'var(--green-mid)', cursor: 'pointer' }}>
                ➕ İşçi Ekle
              </button>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setStep(5)}>← Geri</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} onClick={() => setStep(7)}>Devam →</button>
            </div>
            <div onClick={() => setStep(7)} style={{ textAlign: 'center', fontSize: '0.82rem', color: '#888', cursor: 'pointer', marginTop: 8 }}>İşçi yok, atla →</div>
          </div>
        )}

        {/* STEP 7 — Done */}
        {step === 7 && (
          <div style={{ textAlign: 'center', paddingTop: 40 }}>
            <div style={{ fontSize: '4rem', marginBottom: 16 }}>🎉</div>
            <h1 style={{ fontFamily: 'Playfair Display', color: 'var(--green-dark)', margin: '0 0 8px' }}>{t('onboarding.step_done_title')}</h1>
            <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: 24 }}>{t('onboarding.step_done_subtitle')}</p>
            <div style={{ textAlign: 'left', marginBottom: 24 }}>
              <div style={{ padding: '6px 0', fontSize: '0.85rem' }}>✅ Çiftlik bilgileri kaydedildi</div>
              <div style={{ padding: '6px 0', fontSize: '0.85rem' }}>{fields.length > 0 ? `✅ ${fields.length} tarla eklendi` : '⏭️ Tarla atlandı'}</div>
              <div style={{ padding: '6px 0', fontSize: '0.85rem' }}>✅ {selectedProducts.size} ürün seçildi</div>
              <div style={{ padding: '6px 0', fontSize: '0.85rem' }}>{workers.length > 0 ? `✅ ${workers.length} işçi eklendi` : '⏭️ İşçi atlandı'}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => { finish(); navigate('/harvest'); }}>
                🌾 İlk Hasat Ekle
              </button>
              <button className="btn" style={{ width: '100%', justifyContent: 'center', background: '#f0f7ee' }} onClick={() => { finish(); navigate('/purchases'); }}>
                🛒 İlk Alım Ekle
              </button>
              <button className="btn" style={{ width: '100%', justifyContent: 'center', background: '#f0f0f0' }} onClick={finish}>
                📊 Dashboard'a Git
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
